/*******************************************

* [COMPONENT]: CART DRAWER

  * Internal events:
    * - l:cart:updated: Fired when the cart is updated
    * - l:cart:discount:applied: Fired when a discount code is applied
  * External events
    * - l:product:added: Fired when a product is added to the cart
    * - l:cart:error: Fired when a cart error occurs
    *   ++ add 2 items into cart while product has only 1 item
********************************************/

class LTAMainCart extends HTMLElement {
	constructor() {
		super()

		this.updatedVariants = []
		this._onQuantityChange = l_debounce(this._onQuantityChange.bind(this), 200)
		this._onRemoveLineItem = this._onRemoveLineItem.bind(this)
		this._onCartUpdate = this._onCartUpdate.bind(this)
	}

	connectedCallback() {
		this.updatedVariants = []

		this.addEventListener('change', this._onQuantityChange)
		this.addEventListener('click', this._onRemoveLineItem)

		document.addEventListener('l:product:added', this._onCartUpdate)
		document.addEventListener('l:cart:error', this._onCartUpdate)
		document.addEventListener('l:cart:discount:applied', this._onCartUpdate)
	}

	drawer_close() {
		this.dispatchEvent(
			new CustomEvent('l:cart:closed', {
				bubbles: true,
				detail: { updatedVariants: this.updatedVariants },
			})
		)

		this.updatedVariants = []
	}

	_onRemoveLineItem(event) {
		const removeBtn = event.target.closest('[data-remove-line-item]')
		if (!removeBtn) return

		event.preventDefault()

		const lineItemEl = event.target.closest('[data-drawer-line-item]')
		if (lineItemEl) {
			this._updateCartLine(lineItemEl, 0)
		}
	}

	_onQuantityChange(event) {
		const lineItemEl = event.target.closest('[data-drawer-line-item]')
		if (!lineItemEl) return

		const quantity = parseInt(event.target.value || 1, 10)

		this._updateCartLine(lineItemEl, quantity)
	}

	_updateCartLine(lineItemEl, quantity) {
		if (!lineItemEl) return

		this.classList.add('is-loading')

		const line = parseInt(lineItemEl.dataset.index, 10)
		const variantId = lineItemEl.dataset.variantId

		ltaCartService
			.changeCartLineItem({
				line,
				quantity,
				sections_url: window.location.pathname,
				sections: this._getSectionsToRender().map((section) => section.id),
			})
			.then((cart) => {
				if (cart?.status === 422) {
					this._handleCartError(cart, lineItemEl)

					return
				}

				this._updateCartData(cart.sections[this.dataset.sectionId])

				this.dispatchEvent(new CustomEvent('l:cart:updated', { detail: { cart }, bubbles: true }))
				this.updatedVariants.push(variantId)
			})
			.catch((error) => {
				console.error(error)
			})
			.finally(() => {
				this.classList.remove('is-loading')
			})
	}

	_handleCartError(error = { description: '' }, lineItemEl) {
		// Add error message to line item
		const errorMessageEl = lineItemEl.querySelector('[data-error-message]')

		if (errorMessageEl) {
			errorMessageEl.innerHTML = error.description
			errorMessageEl.parentElement.classList.remove('l-hidden')
		}

		// Dispatch event
		// this.dispatchEvent(
		// 	new CustomEvent('l:cart:error', {
		// 		detail: { id: 'cart-drawer', description: error.description },
		// 		bubbles: true,
		// 	})
		// )
	}

	_onCartUpdate() {
		this.classList.add('is-loading')

		fetch(`${window.LTATheme.routes.cart_url}?section_id=${this.dataset.sectionId}`)
			.then((res) => res.text())
			.then((html) => {
				this._updateCartData(html)
			})
			.finally(() => {
				this.classList.remove('is-loading')
			})
	}

	_updateCartData(html) {
		const newCartDrawerHTML = l_getSectionDOM(html, '#main-cart')
		this.classList.toggle('is-empty', newCartDrawerHTML?.classList.contains('is-empty'))

		const selectors = [
			'[data-cart-count]',
			'[data-cart-inner]',
			'[data-cart-subtotal]',
			'[data-discount-code-container]',
			'#cart-drawer-form',
			'[data-cart-footer]',
		]

		selectors.forEach((selector) => {
			const newSection = newCartDrawerHTML?.querySelector(selector)
			const oldSection = this.querySelector(selector)
			if (newSection && oldSection) oldSection.innerHTML = newSection.innerHTML
		})
	}

	_getSectionsToRender() {
		return [
			{
				id: this.dataset.sectionId,
				selector: '.shopify-section',
			},
		]
	}
}

customElements.define('l-main-cart', LTAMainCart)

/*******************************************

* [COMPONENT]: CART DRAWER

  * Internal events:
    * - l:cart:updated: Fired when the cart is updated
    * - l:cart:discount:applied: Fired when a discount code is applied
  * External events
    * - l:product:added: Fired when a product is added to the cart
    * - l:cart:error: Fired when a cart error occurs
    *   ++ add 2 items into cart while product has only 1 item
********************************************/

class LTACartDrawer extends LTADrawer {
	constructor() {
		super()

		this.updatedVariants = []
		this._onQuantityChange = l_debounce(this._onQuantityChange.bind(this), 200)
		this._onRemoveLineItem = this._onRemoveLineItem.bind(this)
		this._onCartUpdate = this._onCartUpdate.bind(this)
	}

	connectedCallback() {
		if (super.connectedCallback) super.connectedCallback()

		this.updatedVariants = []

		this.addEventListener('change', this._onQuantityChange)
		this.addEventListener('click', this._onRemoveLineItem)

		document.addEventListener('l:product:added', this._onCartUpdate)
		document.addEventListener('l:cart:error', this._onCartUpdate)
		document.addEventListener('l:cart:discount:applied', this._onCartUpdate)
	}

	drawer_close() {
		if (super.drawer_close) super.drawer_close()

		this.dispatchEvent(
			new CustomEvent('l:cart:closed', {
				bubbles: true,
				detail: { updatedVariants: this.updatedVariants },
			})
		)

		this.updatedVariants = []
	}

	_onRemoveLineItem(event) {
		const removeBtn = event.target.closest('[data-remove-line-item]')
		if (!removeBtn) return

		event.preventDefault()

		const lineItemEl = event.target.closest('[data-drawer-line-item]')
		if (lineItemEl) {
			this._updateCartLine(lineItemEl, 0)
		}
	}

	_onQuantityChange(event) {
		const lineItemEl = event.target.closest('[data-drawer-line-item]')
		if (!lineItemEl) return

		const quantity = parseInt(event.target.value || 1, 10)

		this._updateCartLine(lineItemEl, quantity)
	}

	_updateCartLine(lineItemEl, quantity) {
		if (!lineItemEl) return

		this.classList.add('is-loading')

		const line = parseInt(lineItemEl.dataset.index, 10)
		const variantId = lineItemEl.dataset.variantId

		ltaCartService
			.changeCartLineItem({
				line,
				quantity,
				sections_url: window.location.pathname,
				sections: this._getSectionsToRender().map((section) => section.id),
			})
			.then((cart) => {
				if (cart?.status === 422) {
					this._handleCartError(cart, lineItemEl)

					return
				}

				this._updateCartData(cart.sections[this.dataset.sectionId])

				this.dispatchEvent(new CustomEvent('l:cart:updated', { detail: { cart }, bubbles: true }))
				this.updatedVariants.push(variantId)
			})
			.catch((error) => {
				console.error(error)
			})
			.finally(() => {
				this.classList.remove('is-loading')
			})
	}

	_handleCartError(error = { description: '' }, lineItemEl) {
		// Add error message to line item
		const errorMessageEl = lineItemEl.querySelector('[data-error-message]')

		if (errorMessageEl) {
			errorMessageEl.innerHTML = error.description
			errorMessageEl.parentElement.classList.remove('l-hidden')
		}

		// Dispatch event
		// this.dispatchEvent(
		// 	new CustomEvent('l:cart:error', {
		// 		detail: { id: 'cart-drawer', description: error.description },
		// 		bubbles: true,
		// 	})
		// )
	}

	_onCartUpdate() {
		this.drawer_open?.()
		this.classList.add('is-loading')

		fetch(`${window.LTATheme.routes.cart_url}?section_id=${this.dataset.sectionId}`)
			.then((res) => res.text())
			.then((html) => {
				this._updateCartData(html)
			})
			.finally(() => {
				this.classList.remove('is-loading')
			})
	}

	_updateCartData(html) {
		if (!this.classList.contains('is-active')) this.drawer_open?.()

		const newCartDrawerHTML = l_getSectionDOM(html, '#cart-drawer')
		this.classList.toggle('is-empty', newCartDrawerHTML?.classList.contains('is-empty'))

		const selectors = [
			'[data-cart-count]',
			'[data-cart-inner]',
			'[data-cart-subtotal]',
			'[data-discount-code-container]',
			'#cart-drawer-form',
			'[data-cart-footer]',
		]

		selectors.forEach((selector) => {
			const newSection = newCartDrawerHTML?.querySelector(selector)
			const oldSection = this.querySelector(selector)
			if (newSection && oldSection) oldSection.innerHTML = newSection.innerHTML
		})
	}

	_getSectionsToRender() {
		return [
			{
				id: this.dataset.sectionId,
				selector: '.shopify-section',
			},
		]
	}
}

customElements.define('l-cart-drawer', LTACartDrawer)

/*******************************************
 * [COMPONENT]: SHIPPING BAR
 ********************************************/

if (!customElements.get('l-shipping-bar')) {
	class LTAShippingBar extends HTMLElement {
		constructor() {
			super()
			this.elements = {}
		}

		connectedCallback() {
			this.cacheElements()
			this.render()
		}

		cacheElements() {
			this.elements.label = this.querySelector('[data-label]')
			this.elements.progressBar = this.querySelector('[data-progress-bar]')
			this.elements.srOnly = this.querySelector('[data-sr-only]')
		}

		getCartData() {
			const { cartTotal, thresholdAmount, cartCurrency, shopCurrency, progressMessage, successMessage } = this.dataset
			return {
				cartTotal: parseFloat(cartTotal),
				thresholdAmount: parseFloat(thresholdAmount),
				cartCurrency,
				shopCurrency,
				progressMessage,
				successMessage,
			}
		}

		getProgressData(cartTotal, thresholdAmount, shopCurrency, cartCurrency) {
			const convertedThreshold = Currency.convert(thresholdAmount, shopCurrency, cartCurrency)
			const remaining = Math.max(0, convertedThreshold - cartTotal)
			const progress = Math.min((cartTotal / convertedThreshold) * 100, 100)
			return { progress, remaining, convertedThreshold }
		}

		render() {
			const { label, progressBar, srOnly } = this.elements
			const { cartTotal, thresholdAmount, shopCurrency, cartCurrency, progressMessage, successMessage } =
				this.getCartData()

			if (isNaN(cartTotal) || isNaN(thresholdAmount) || !progressBar || !label || !srOnly) {
				return
			}

			const { progress, remaining } = this.getProgressData(cartTotal, thresholdAmount, shopCurrency, cartCurrency)

			progressBar.style.setProperty('--l-progress-bar-width', `${progress}%`)
			label.innerHTML = progress < 100 ? progressMessage.replace('[money]', l_formatMoney(remaining)) : successMessage
			progressBar.setAttribute('aria-valuenow', progress.toFixed(0))
			srOnly.innerHTML = `${progress.toFixed(0)}%`
		}
	}

	customElements.define('l-shipping-bar', LTAShippingBar)
}

/*******************************************
 * [COMPONENT]: DISCOUNT CODE
 ********************************************/

class LTAMainCartDiscountCode extends HTMLElement {
	constructor() {
		super()

		this.onSubmit = this.onSubmit.bind(this)
		this.onInput = l_debounce(this.onInput.bind(this), 200)
	}

	connectedCallback() {
		this.inputEl = this.querySelector('[data-discount-input]')
		this.submitEl = this.querySelector('[data-submit]')
		this.removeDiscountEls = this.querySelectorAll('[data-remove-discount]')
		this.cartDrawerEl = document.getElementById('cart-drawer')

		this.parseDiscountCodes()

		this.addRemoveDiscountListeners()

		if (this.submitEl) this.submitEl.addEventListener('click', this.onSubmit)
		if (this.inputEl) this.inputEl.addEventListener('input', this.onInput)
	}

	parseDiscountCodes() {
		this.discountCodes = (this.dataset.discountCodeCodes || '').split(',').filter(Boolean)
		this.discountCodeCount = this.discountCodes.length
	}

	onInput() {
		if (this.submitEl && this.inputEl) {
			this.submitEl.disabled = this.inputEl.value.trim().length === 0
		}
	}

	onSubmit(event) {
		event.preventDefault()

		if (this.cartDrawerEl) this.cartDrawerEl.classList.add('is-loading')
		if (this.submitEl) this.submitEl.classList.add('is-loading')

		const codes = Array.from(new Set([...(this.discountCodes || []), this.inputEl?.value || ''])).filter(Boolean)
		const discount = codes.join(',')

		this.updateCodes(discount)
	}

	addRemoveDiscountListeners() {
		this.removeDiscountEls.forEach((el) => {
			el.addEventListener('click', () => {
				el.classList.add('l-button-loading')
				const discountCode = el.dataset.removeDiscount
				const newDiscountCodes = this.discountCodes.filter((code) => code !== discountCode).join(',')
				this.updateCodes(newDiscountCodes)
			})
		})
	}

	updateCodes(discount) {
		fetch(window.LTATheme.routes.cart_update_url, {
			...l_fetchConfig(),
			body: JSON.stringify({ discount }),
		})
			.then((res) => res.json())
			.then((response) => {
				this.dispatchEvent(
					new CustomEvent('l:cart:discount:applied', {
						detail: { response },
						bubbles: true,
					})
				)
			})
			.finally(() => {
				l_debounce(() => {
					if (this.submitEl) this.submitEl.classList.remove('is-loading')
					if (this.inputEl) this.inputEl.value = ''
				}, 500)()
			})
	}
}

customElements.define('l-cart-discount-code', LTAMainCartDiscountCode)

/*******************************************
 * [COMPONENT]: CART NOTE
 ********************************************/

class LTACartNote extends HTMLElement {
	constructor() {
		super()

		this.boundHandleInput = l_debounce(this.handleInput.bind(this), 400)
	}

	connectedCallback() {
		this.textAreaEl = this.querySelector('textarea')
		if (this.textAreaEl) {
			this.textAreaEl.addEventListener('input', this.boundHandleInput)
		}
	}

	disconnectedCallback() {
		if (this.textAreaEl) {
			this.textAreaEl.removeEventListener('input', this.boundHandleInput)
		}
	}

	handleInput() {
		const note = this.textAreaEl?.value || ''
		fetch(window.LTATheme.routes.cart_update_url, {
			...l_fetchConfig(),
			body: JSON.stringify({ note }),
		}).then((res) => res.json())
	}
}

customElements.define('l-cart-note', LTACartNote)

